import { useState, useEffect, useCallback } from "react";
import { api, errMsg } from "../../lib/api";
import StatusBadge from "../../components/StatusBadge";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { Star, Dumbbell } from "lucide-react";

function IronmanManager() {
  const [finishers, setFinishers] = useState([]);
  const [props, setProps] = useState([]);
  const [q, setQ] = useState("");

  const load = useCallback(() => {
    api.get("/admin/ironman").then(({ data }) => setFinishers(data));
  }, []);
  useEffect(() => {
    load();
    api.get("/admin/properties").then(({ data }) => setProps(data));
  }, [load]);

  const finisherIds = new Set(finishers.map((f) => f.property_id));
  const results = q.trim()
    ? props.filter((p) => p.status !== "Withdrawn" && !finisherIds.has(p.id) && p.name.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 8)
    : [];

  const award = async (p) => {
    try {
      await api.post(`/admin/ironman/${p.id}`);
      toast.success(`${p.name} marked as Lazy Ironman finisher (+25)`);
      setQ("");
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const undo = async (f) => {
    try {
      await api.delete(`/admin/ironman/${f.property_id}`);
      toast.success(`Undone — ${f.property_name} no longer marked as finisher`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="mt-10" data-testid="ironman-section">
      <p className="text-sm text-slate-600 mb-1 flex items-center gap-2"><Dumbbell className="w-4 h-4 text-[#c8102e]" /><span className="font-semibold text-[#1a1f36]">Lazy Ironman Finisher Bonus</span></p>
      <p className="text-xs text-slate-500 mb-3">Mark any team (property or Support floor) that completed the Lazy Ironman by July 31. One-time +25 raw points — added to task points for properties, straight to visit totals for floors. Undo anytime.</p>
      <div className="max-w-md">
        <Input placeholder="Search a team to mark as finisher…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="ironman-search" />
        {results.length > 0 && (
          <div className="mt-1 border border-slate-200 rounded-md divide-y" data-testid="ironman-results">
            {results.map((p) => (
              <div key={p.id} className="px-3 py-2 flex items-center justify-between gap-2 text-sm">
                <div className="min-w-0">
                  <span className="font-medium text-[#1a1f36]">{p.name}</span>
                  <span className="text-xs text-slate-400 ml-2">{p.division}</span>
                </div>
                <Button size="sm" className="sw-red hover:bg-red-700 text-white shrink-0" onClick={() => award(p)} data-testid={`ironman-award-${p.id}`}>Mark finished +25</Button>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="mt-4 space-y-2">
        {finishers.length === 0 && <p className="text-sm text-slate-500 py-6 text-center border border-dashed border-slate-300 rounded-md">No Lazy Ironman finishers marked yet.</p>}
        {finishers.map((f) => (
          <div key={f.id} className="border border-slate-200 bg-[#f8f9fc] rounded-md px-4 py-2.5 flex items-center gap-3" data-testid={`ironman-row-${f.property_id}`}>
            <Dumbbell className="w-4 h-4 text-slate-500 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm text-[#1a1f36]">{f.property_name} <span className="text-[#c8102e] font-semibold">+25</span></div>
              <div className="text-xs text-slate-500">{f.division} · marked {new Date(f.awarded_at).toLocaleDateString()}{f.awarded_by ? ` by ${f.awarded_by}` : ""}</div>
            </div>
            <Button size="sm" variant="ghost" className="text-slate-500 hover:text-red-600" onClick={() => undo(f)} data-testid={`ironman-undo-${f.property_id}`}>Undo</Button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminBonuses() {
  const [rows, setRows] = useState([]);
  const [preview, setPreview] = useState(null);
  const load = () => api.get("/admin/bonuses").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  const review = async (id, status) => {
    await api.put(`/admin/bonuses/${id}`, { status });
    toast.success(status === "approved" ? "Commander Bonus verified (+15)" : "Bonus rejected");
    load();
  };

  return (
    <div>
      <p className="text-sm text-slate-600 mb-3 flex items-center gap-2"><Star className="w-4 h-4 text-[#c8102e]" />Verify a Regional's photo completing one task with the team. Verified = +15 raw task points.</p>
      <div className="space-y-2">
        {rows.length === 0 && <p className="text-sm text-slate-500 py-10 text-center border border-dashed border-slate-300 rounded-md">No Commander Bonus submissions yet.</p>}
        {rows.map((b) => (
          <div key={b.id} className="border border-slate-200 rounded-md p-3 flex items-center gap-3" data-testid={`bonus-row-${b.id}`}>
            {b.photo && <img src={b.photo} alt="" className="w-14 h-14 rounded object-cover cursor-pointer" onClick={() => setPreview(b.photo)} />}
            <div className="flex-1">
              <div className="font-medium text-sm">{b.property_name}</div>
              <div className="text-xs text-slate-500">Regional: {b.regional_name} · {new Date(b.created_at).toLocaleString()}</div>
            </div>
            <StatusBadge status={b.status} />
            {b.status === "pending" && (
              <div className="flex gap-1">
                <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => review(b.id, "approved")} data-testid={`bonus-verify-${b.id}`}>Verify +15</Button>
                <Button size="sm" variant="outline" className="text-red-600" onClick={() => review(b.id, "rejected")} data-testid={`bonus-reject-${b.id}`}>Reject</Button>
              </div>
            )}
          </div>
        ))}
      </div>
      <IronmanManager />
      {preview && <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6" onClick={() => setPreview(null)}><img src={preview} alt="" className="max-h-full max-w-full rounded-md" /></div>}
    </div>
  );
}
